import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Download, Mail, Search, Trash2, Upload, UserPlus } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { Input } from '../../../ui/input';
import { newSelect } from '../../../ui/newSelect';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import ImportEmployeesDialog from '../dialogs/ImportEmployeesDialog';
import { downloadInvitationTemplateCsv, getInvitationDraftLineStatuses, parseInvitationCsvText } from '../surveyInvitationCsv';
import { getInvitationStatusBadgeProps, prettyDate } from '../surveyUiUtils';

/** Hide resend / revoke actions once a final response exists (status or completion timestamps). */
const invitationSurveySubmitted = (row) => {
  const s = String(row?.status || '').toLowerCase();
  if (s === 'completed' || s === 'submitted') return true;
  if (row?.completedAt || row?.submittedAt) return true;
  return false;
};

const statusLabel = (status) => {
  const key = String(status || '').toLowerCase();
  const map = {
    completed: 'Completed',
    submitted: 'Completed',
    started: 'In progress',
    in_progress: 'In progress',
    sent: 'Not started',
    pending: 'Not sent',
    draft: 'Not sent',
    not_sent: 'Not sent',
    revoked: 'Revoked',
    excluded: 'Excluded',
  };
  if (map[key]) return map[key];
  const raw = String(status || 'Unknown').replace(/_/g, ' ').trim();
  return raw.replace(/\b\w/g, (char) => char.toUpperCase());
};

const EmployeesTab = ({
  campaign,
  facilities,
  onSaveFacilities,
  onAddFacility,
  facilityOptions,
  invitationRows,
  invitationDraft,
  onInvitationDraftChange,
  onSaveInvitations,
  onBulkImportInvitations,
  onDeleteInvitation,
  onResendInvitation,
  loading,
}) => {
  const [query, setQuery] = useState('');
  const [newFacilityId, setNewFacilityId] = useState(null);
  const [facilitySelectOpen, setFacilitySelectOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deletingInvitationId, setDeletingInvitationId] = useState(null);
  const [resendingInvitationId, setResendingInvitationId] = useState(null);
  const [deleteInviteModal, setDeleteInviteModal] = useState(null);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const NewSelect = newSelect;

  const hasInvitationDraft = Boolean(String(invitationDraft || '').trim());
  const draftParse = useMemo(() => parseInvitationCsvText(invitationDraft), [invitationDraft]);
  const draftLineStatuses = useMemo(() => getInvitationDraftLineStatuses(invitationDraft), [invitationDraft]);
  const validDraftCount = draftParse.invitations.length;
  const invalidDraftCount = draftParse.skipped.length;
  const showDraftValidation = hasInvitationDraft && (invalidDraftCount > 0 || validDraftCount === 0);

  useEffect(() => {
    if (!hasInvitationDraft) setSaveAttempted(false);
  }, [hasInvitationDraft]);

  const handleSaveInvitations = () => {
    setSaveAttempted(true);
    if (!validDraftCount) return;
    setSaveAttempted(false);
    onSaveInvitations?.();
  };

  const closeDeleteInviteModal = () => {
    if (deletingInvitationId) return;
    setDeleteInviteModal(null);
  };

  const confirmDeleteInvite = async () => {
    if (!deleteInviteModal?.invitationId || !onDeleteInvitation) return;
    setDeletingInvitationId(deleteInviteModal.invitationId);
    try {
      await onDeleteInvitation(deleteInviteModal.invitationId);
      setDeleteInviteModal(null);
    } finally {
      setDeletingInvitationId(null);
    }
  };

  const addedFacilityIds = useMemo(
    () =>
      new Set(
        (facilities || [])
          .map((row) => row?.id)
          .filter((id) => id != null && String(id) !== ''),
      ),
    [facilities],
  );

  const availableFacilityOptions = useMemo(() => {
    const options = Array.isArray(facilityOptions) ? facilityOptions : [];
    return options.filter((opt) => opt?.value != null && !addedFacilityIds.has(String(opt.value)));
  }, [facilityOptions, addedFacilityIds]);

  useEffect(() => {
    if (!newFacilityId) return;
    const stillAvailable = availableFacilityOptions.some((opt) => String(opt.value) === String(newFacilityId));
    if (!stillAvailable) setNewFacilityId(null);
  }, [newFacilityId, availableFacilityOptions]);

  useEffect(() => {
    if (availableFacilityOptions.length === 0) setFacilitySelectOpen(false);
  }, [availableFacilityOptions.length]);

  const filteredInvitations = useMemo(() => {
    if (!query) return invitationRows;
    const q = query.toLowerCase();
    return invitationRows.filter((row) => {
      const name = String(row.employeeName || row.name || '').toLowerCase();
      const email = String(row.employeeEmail || row.email || '').toLowerCase();
      const location = String(row.locationName || row.location || row.facilityName || '').toLowerCase();
      return name.includes(q) || email.includes(q) || location.includes(q);
    });
  }, [invitationRows, query]);

  const isClosed = String(campaign?.status || '').toLowerCase() === 'closed';
  const invitedCount = Number(campaign?.invitedCount || campaign?.invitationCount || invitationRows.length || 0);
  const completedCount = Number(campaign?.completedCount || campaign?.submittedCount || 0);
  const outstandingCount = Math.max(invitedCount - completedCount, 0);

  return (
    <div className='grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-6'>
      <div className='space-y-4'>
        <Card className={facilitySelectOpen ? 'relative z-50' : undefined}>
          <CardHeader>
            <CardTitle className='text-sm flex items-center justify-between'>
              Office locations &amp; FTE
              <Badge variant='outline' size='sm'>
                {facilities.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            <p className='text-xs text-muted-foreground'>
              FTE counts are used to extrapolate respondent totals to your full workforce.
            </p>
            {facilities.map((facility, index) => (
              <div key={facility.id || facility.name || index} className='grid grid-cols-[1fr_88px] gap-2 items-center'>
                <div className='text-sm'>{facility.name || `Facility ${index + 1}`}</div>
                <Input
                  type='number'
                  value={facility.fte || 0}
                  onChange={(event) => {
                    const next = [...facilities];
                    next[index] = { ...next[index], fte: Number(event.target.value || 0) };
                    onSaveFacilities(next, true);
                  }}
                  className='h-8 text-right'
                />
              </div>
            ))}
            {availableFacilityOptions.length > 0 ? (
              <div className='grid grid-cols-[1fr_auto] gap-2 items-center'>
                <NewSelect
                  options={availableFacilityOptions}
                  value={newFacilityId}
                  onChange={setNewFacilityId}
                  onOpenChange={setFacilitySelectOpen}
                  placeholder='Select facility...'
                />
                <Button
                  variant='outline'
                  size='sm'
                  disabled={!newFacilityId}
                  onClick={() => {
                    onAddFacility(newFacilityId);
                    setNewFacilityId(null);
                  }}>
                  <UserPlus size={14} strokeWidth={1.5} />
                  Add location
                </Button>
              </div>
            ) : null}
            <Button size='sm' className='w-full' onClick={() => onSaveFacilities(facilities)} loading={Boolean(loading?.facilities)}>
              Save locations
            </Button>
          </CardContent>
        </Card>

        {invitedCount === 0 ? (
          <Alert>
            <AlertTriangle size={14} strokeWidth={1.5} />
            <div>
              <AlertTitle>Setup required</AlertTitle>
              <AlertDescription>
                Import your employee list, then send invitations. Each employee gets a unique survey link.
              </AlertDescription>
            </div>
          </Alert>
        ) : null}
      </div>

      <Card>
        <CardHeader className='flex-row items-center justify-between space-y-0'>
          <div>
            <CardTitle className='text-base'>Employee invitations</CardTitle>
            <p className='text-xs text-muted-foreground mt-1'>
              {invitedCount} invited · {completedCount} completed · {outstandingCount} outstanding
            </p>
          </div>
          {!isClosed ? (
            <div className='flex items-center gap-2'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => downloadInvitationTemplateCsv(campaign?.name, facilities)}>
                <Download size={14} strokeWidth={1.5} />
                Template CSV
              </Button>
              <Button
                type='button'
                size='sm'
                onClick={() => setImportDialogOpen(true)}
                disabled={typeof onBulkImportInvitations !== 'function'}>
                <Upload size={14} strokeWidth={1.5} />
                Import employees
              </Button>
            </div>
          ) : null}
        </CardHeader>

        <div className='flex items-center gap-3 px-5 py-3 border-b border-border'>
          <div className='relative flex-1 max-w-sm'>
            <Search size={14} strokeWidth={1.5} className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground' />
            <Input
              placeholder='Search name or email...'
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className='pl-9 h-8 text-sm'
            />
          </div>
          <Badge variant='outline' size='sm'>
            {filteredInvitations.length} shown
          </Badge>
        </div>

        <Table wrapperClassName='rounded-none border-0 shadow-none'>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead>Completed</TableHead>
              <TableHead className='text-right'>Reminders</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredInvitations.map((row, index) => (
              <TableRow key={row.id || row.invitationId || index}>
                <TableCell>
                  <div className='font-medium'>{row.employeeName || row.name || '-'}</div>
                  <div className='text-xs text-muted-foreground'>{row.employeeEmail || row.email || '-'}</div>
                </TableCell>
                <TableCell className='text-sm'>{row.locationName || row.location || row.facilityName || '-'}</TableCell>
                <TableCell className='whitespace-nowrap align-middle'>
                  <Badge {...getInvitationStatusBadgeProps(row.status)} size='default'>
                    {statusLabel(row.status)}
                  </Badge>
                </TableCell>
                <TableCell className='text-xs text-muted-foreground'>
                  {prettyDate(row.sentAt || row.invitedAt || row.createdAt)}
                </TableCell>
                <TableCell className='text-xs text-muted-foreground'>{prettyDate(row.completedAt || row.submittedAt)}</TableCell>
                <TableCell className='text-right tabular-nums text-xs'>{row.reminderCount || 0}</TableCell>
                <TableCell className='text-right'>
                  {invitationSurveySubmitted(row) ? null : (
                    <div className='flex items-center gap-1 justify-end'>
                      {!isClosed && (
                        <Button
                          type='button'
                          variant='ghost'
                          size='icon'
                          title='Resend invitation'
                          disabled={!row.invitationId || !onResendInvitation}
                          loading={resendingInvitationId === row.invitationId}
                          onClick={async () => {
                            if (!row.invitationId || !onResendInvitation) return;
                            setResendingInvitationId(row.invitationId);
                            try {
                              await onResendInvitation(row.invitationId);
                            } finally {
                              setResendingInvitationId(null);
                            }
                          }}>
                          <Mail size={14} strokeWidth={1.5} />
                        </Button>
                      )}
                      {String(row.status || '').toLowerCase() !== 'revoked' && (
                        <Button
                          type='button'
                          variant='ghost'
                          size='icon'
                          title='Remove invitation'
                          disabled={!row.invitationId || !onDeleteInvitation}
                          loading={deletingInvitationId === row.invitationId}
                          onClick={() => {
                            if (!row.invitationId || !onDeleteInvitation) return;
                            const displayLabel = row.employeeName || row.employeeEmail || 'this employee';
                            setDeleteInviteModal({ invitationId: row.invitationId, displayLabel });
                          }}>
                          <Trash2 size={14} strokeWidth={1.5} className='text-destructive/70' />
                        </Button>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {!isClosed ? (
          <div className='px-5 py-4 border-t border-border space-y-3'>
            <div>
              <div className='text-sm font-medium text-foreground'>Quick add invitations</div>
              <p className='text-xs text-muted-foreground mt-0.5'>
                One employee per line. Use commas or semicolons between name, email, and office location (same columns as
                the table above).
              </p>
            </div>

            <div className='rounded-lg border border-border overflow-hidden'>
              <Table wrapperClassName='rounded-none border-0 shadow-none'>
                <TableHeader>
                  <TableRow className='bg-muted/40 hover:bg-muted/40'>
                    <TableHead className='w-10 h-9 text-xs'>#</TableHead>
                    <TableHead className='h-9 text-xs'>Name</TableHead>
                    <TableHead className='h-9 text-xs'>Email</TableHead>
                    <TableHead className='h-9 text-xs'>Office location</TableHead>
                    <TableHead className='h-9 text-xs min-w-[120px]'>Status</TableHead>
                  </TableRow>
                </TableHeader>
              </Table>
              <textarea
                className='w-full min-h-[88px] resize-y border-0 border-t border-border px-3 py-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset'
                value={invitationDraft}
                onChange={(event) => onInvitationDraftChange(event.target.value)}
                placeholder={'Jane Doe,jane@example.com,Main office\nJohn Smith,john@example.com,London office'}
                spellCheck={false}
              />
              {draftLineStatuses.length > 0 ? (
                <div className='border-t border-border max-h-40 overflow-y-auto'>
                  <Table wrapperClassName='rounded-none border-0 shadow-none'>
                    <TableBody>
                      {draftLineStatuses.map((line) => (
                        <TableRow
                          key={line.lineNum}
                          className={line.valid ? 'hover:bg-muted/30' : 'bg-danger-bg/40 hover:bg-danger-bg/50'}>
                          <TableCell className='w-10 py-1.5 text-xs text-muted-foreground tabular-nums'>{line.lineNum}</TableCell>
                          <TableCell className='py-1.5 text-sm max-w-[140px] truncate' title={line.employeeName || undefined}>
                            {line.employeeName || '—'}
                          </TableCell>
                          <TableCell className='py-1.5 text-sm max-w-[160px] truncate' title={line.employeeEmail || undefined}>
                            {line.employeeEmail || '—'}
                          </TableCell>
                          <TableCell className='py-1.5 text-sm max-w-[140px] truncate' title={line.location || undefined}>
                            {line.location || '—'}
                          </TableCell>
                          <TableCell className='py-1.5 text-xs max-w-[200px]'>
                            {line.valid ? (
                              <span className='text-success'>Ready</span>
                            ) : (
                              <span className='text-danger' title={line.reason}>
                                {line.reason}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : null}
            </div>

            {showDraftValidation ? (
              <Alert variant='destructive'>
                <AlertTriangle size={14} strokeWidth={1.5} />
                <div>
                  <AlertTitle>
                    {validDraftCount === 0
                      ? 'No valid rows to save'
                      : `${invalidDraftCount} row${invalidDraftCount === 1 ? '' : 's'} need fixing`}
                  </AlertTitle>
                  <AlertDescription>
                    {validDraftCount === 0 ? (
                      <span>Check each line has name, email, and office location separated by commas or semicolons.</span>
                    ) : (
                      <span>
                        Only {validDraftCount} valid row{validDraftCount === 1 ? '' : 's'} will be saved. Fix the lines
                        marked in red above.
                      </span>
                    )}
                    <ul className='mt-2 list-disc pl-4 space-y-0.5 text-xs'>
                      {draftParse.skipped.slice(0, 8).map((item, index) => (
                        <li key={`${item.line}-${index}`}>
                          Line {item.line}: {item.reason}
                        </li>
                      ))}
                      {draftParse.skipped.length > 8 ? <li>…and {draftParse.skipped.length - 8} more</li> : null}
                    </ul>
                  </AlertDescription>
                </div>
              </Alert>
            ) : null}

            {saveAttempted && !validDraftCount && hasInvitationDraft ? (
              <p className='text-xs text-destructive'>Add at least one valid row before saving.</p>
            ) : null}

            <div className='flex flex-wrap items-center gap-2'>
              <Button
                size='sm'
                onClick={handleSaveInvitations}
                disabled={!validDraftCount}
                loading={Boolean(loading?.invitations)}>
                Save invitations{validDraftCount ? ` (${validDraftCount})` : ''}
              </Button>
              {hasInvitationDraft && validDraftCount > 0 && invalidDraftCount > 0 ? (
                <span className='text-xs text-muted-foreground'>
                  {invalidDraftCount} invalid row{invalidDraftCount === 1 ? '' : 's'} will be skipped
                </span>
              ) : null}
            </div>

            {facilities?.length ? (
              <p className='text-xs text-muted-foreground'>
                Office location must match a name from the list on the left (
                {facilities.map((f) => f.name).filter(Boolean).join(', ') || 'add locations first'}).
              </p>
            ) : null}
          </div>
        ) : null}
      </Card>

      <Dialog
        open={Boolean(deleteInviteModal)}
        onOpenChange={(open) => {
          if (!open) closeDeleteInviteModal();
        }}>
        <DialogContent className='sm:max-w-md' onClose={closeDeleteInviteModal}>
          <DialogHeader>
            <DialogTitle>Remove invitation</DialogTitle>
            <DialogDescription>
              {deleteInviteModal
                ? `This will remove the survey invitation for ${deleteInviteModal.displayLabel}. They will no longer receive this campaign link. This action cannot be undone.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className='gap-2 sm:gap-0'>
            <Button type='button' variant='outline' onClick={closeDeleteInviteModal} disabled={Boolean(deletingInvitationId)}>
              Cancel
            </Button>
            <Button
              type='button'
              variant='destructive'
              onClick={confirmDeleteInvite}
              disabled={!deleteInviteModal?.invitationId}
              loading={Boolean(deletingInvitationId)}>
              Remove invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {onBulkImportInvitations ? (
        <ImportEmployeesDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          campaignName={campaign?.name}
          facilities={facilities}
          loading={loading?.invitations}
          onCommit={onBulkImportInvitations}
        />
      ) : null}
    </div>
  );
};

export default EmployeesTab;
